"use client"

import { useSyncExternalStore } from "react"

/**
 * Device-local run history - the "this device" side of the Records menu.
 *
 * Kept in localStorage rather than the server so it works without a wallet and
 * survives being offline. The wallet leaderboard and on-chain inscriptions are
 * the shared/verifiable counterparts; this is the personal record.
 */

export interface HeroStats {
  runs: number
  bestScore: number
  /** Deepest level index reached with this hero (0-based) */
  bestLevelIndex: number
  wins: number
}

export interface PlayerStats {
  runs: number
  bestScore: number
  bestLevelIndex: number
  wins: number
  totalScore: number
  /** Wall-clock ms spent in runs */
  totalPlayMs: number
  /** Per hero-type record, keyed by hero id */
  heroes: Record<string, HeroStats>
  /** ISO timestamp of the most recent recorded run */
  lastPlayedAt: string | null
}

export interface RunResult {
  heroType: string
  score: number
  levelIndex: number
  /** Run duration in ms */
  durationMs: number
  /** True when the run was completed rather than lost */
  won: boolean
}

const STORAGE_KEY = "pelf-player-stats"

const emptyHero = (): HeroStats => ({ runs: 0, bestScore: 0, bestLevelIndex: 0, wins: 0 })

const DEFAULTS: PlayerStats = {
  runs: 0,
  bestScore: 0,
  bestLevelIndex: 0,
  wins: 0,
  totalScore: 0,
  totalPlayMs: 0,
  heroes: {},
  lastPlayedAt: null,
}

const num = (v: unknown, fallback = 0): number =>
  typeof v === "number" && Number.isFinite(v) ? v : fallback

function load(): PlayerStats {
  if (typeof window === "undefined") return DEFAULTS
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULTS
    const p = JSON.parse(raw) as Partial<PlayerStats>
    const heroes: Record<string, HeroStats> = {}
    if (p.heroes && typeof p.heroes === "object") {
      for (const [id, h] of Object.entries(p.heroes as Record<string, Partial<HeroStats>>)) {
        heroes[id] = {
          runs: num(h?.runs),
          bestScore: num(h?.bestScore),
          bestLevelIndex: num(h?.bestLevelIndex),
          wins: num(h?.wins),
        }
      }
    }
    return {
      runs: num(p.runs),
      bestScore: num(p.bestScore),
      bestLevelIndex: num(p.bestLevelIndex),
      wins: num(p.wins),
      totalScore: num(p.totalScore),
      totalPlayMs: num(p.totalPlayMs),
      heroes,
      lastPlayedAt: typeof p.lastPlayedAt === "string" ? p.lastPlayedAt : null,
    }
  } catch {
    return DEFAULTS
  }
}

let stats: PlayerStats = load()
const listeners = new Set<() => void>()

function persist(): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stats))
  } catch {
    // Private browsing / storage full - stats just won't survive reload
  }
}

function emit(): void {
  persist()
  for (const listener of listeners) listener()
}

export function getPlayerStats(): PlayerStats {
  return stats
}

/** Fold a finished run into the local record. Idempotency is the caller's job. */
export function recordRun(result: RunResult): void {
  const hero = stats.heroes[result.heroType] ?? emptyHero()
  stats = {
    runs: stats.runs + 1,
    bestScore: Math.max(stats.bestScore, result.score),
    bestLevelIndex: Math.max(stats.bestLevelIndex, result.levelIndex),
    wins: stats.wins + (result.won ? 1 : 0),
    totalScore: stats.totalScore + result.score,
    totalPlayMs: stats.totalPlayMs + Math.max(0, result.durationMs),
    heroes: {
      ...stats.heroes,
      [result.heroType]: {
        runs: hero.runs + 1,
        bestScore: Math.max(hero.bestScore, result.score),
        bestLevelIndex: Math.max(hero.bestLevelIndex, result.levelIndex),
        wins: hero.wins + (result.won ? 1 : 0),
      },
    },
    lastPlayedAt: new Date().toISOString(),
  }
  emit()
}

export function resetPlayerStats(): void {
  stats = { ...DEFAULTS, heroes: {} }
  emit()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Reactive accessor for components. */
export function usePlayerStats(): PlayerStats {
  return useSyncExternalStore(subscribe, getPlayerStats, () => DEFAULTS)
}

/** "3h 12m" / "12m" / "48s" - compact enough for a stat row. */
export function formatPlayTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  if (totalSeconds < 60) return `${totalSeconds}s`
  const minutes = Math.floor(totalSeconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}
